/**
 * Détection des vidéos YouTube / Vimeo dans un texte libre.
 *
 * Miroir de backend/app/media.py — si on ajoute un fournisseur ici,
 * l'ajouter aussi côté Python (l'aperçu LinkedIn s'en sert pour choisir
 * l'image OpenGraph).
 *
 * Aucune clé d'API :
 *   - YouTube sert ses miniatures sur img.youtube.com sans authentification
 *   - Vimeo expose oEmbed publiquement, avec CORS ouvert
 */

// youtube.com/watch?v=ID · youtu.be/ID · /embed/ID · /shorts/ID · /live/ID
const YOUTUBE_RX =
  /(?:youtube\.com\/(?:watch\?(?:[^\s]*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/i;

// vimeo.com/123456789 (avec variantes /video/, /channels/x/, /groups/x/videos/)
const VIMEO_RX =
  /vimeo\.com\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/)?(\d{6,})/i;

/**
 * Première vidéo trouvée dans le texte, ou null.
 * YouTube prioritaire si le post cite les deux.
 * @returns {{provider: "youtube"|"vimeo", videoId: string} | null}
 */
export function findVideo(text) {
  if (!text || typeof text !== "string") return null;
  const yt = text.match(YOUTUBE_RX);
  if (yt) return { provider: "youtube", videoId: yt[1] };
  const vm = text.match(VIMEO_RX);
  if (vm) return { provider: "vimeo", videoId: vm[1] };
  return null;
}

/** URL de la miniature YouTube. hqdefault existe pour 100% des vidéos. */
export function youtubeThumbnail(videoId, { hq = true } = {}) {
  return `https://img.youtube.com/vi/${videoId}/${hq ? "hqdefault" : "default"}.jpg`;
}

/** Miniature Vimeo via oEmbed public. Renvoie null si indisponible. */
export async function fetchVimeoThumbnail(videoId, { signal } = {}) {
  try {
    const res = await fetch(
      `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(
        `https://vimeo.com/${videoId}`
      )}`,
      { signal }
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data?.thumbnail_url || null;
  } catch {
    return null; // hors-ligne, CORS, abort… : on retombe sur le lecteur direct
  }
}

/** URL du lecteur intégré. nocookie pour YouTube (pas de tracking avant lecture). */
export function embedUrl({ provider, videoId }, { autoplay = false } = {}) {
  if (provider === "youtube") {
    return `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1${
      autoplay ? "&autoplay=1" : ""
    }`;
  }
  if (provider === "vimeo") {
    return `https://player.vimeo.com/video/${videoId}?dnt=1${
      autoplay ? "&autoplay=1" : ""
    }`;
  }
  return null;
}

/** URL publique de la page vidéo (lien de repli). */
export function watchUrl({ provider, videoId }) {
  if (provider === "youtube") return `https://www.youtube.com/watch?v=${videoId}`;
  if (provider === "vimeo") return `https://vimeo.com/${videoId}`;
  return null;
}

export const PROVIDER_LABEL = { youtube: "YouTube", vimeo: "Vimeo" };
